import React, { Fragment, useCallback, useEffect, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { ArrowDownTrayIcon, ClipboardDocumentIcon, UserGroupIcon } from '@heroicons/react/24/outline';
import {
  documentSignatureService,
  type DocumentSignatureDto,
  type DocumentSignerDto,
  type SignatureEvidenceDto,
  SignatureRequestStatusCode,
  SignatureStatusCode,
  SignatureTypeCode,
} from '../../../services/documentSignatureService';
import { useToast } from '../../UI/ToastProvider';
import { Button } from '../../UI/Button';
import { formatDateTimeSafely } from '../../../utils/dateUtils';
import useCopyText from '../../../hooks/useCopyText';

export interface DocumentSignersModalProps {
  isOpen: boolean;
  onClose: () => void;
  documentId: string;
  onChanged: () => void | Promise<void>;
}

const SIGNATURE_TYPE_LABEL: Record<number, string> = {
  [SignatureTypeCode.Certificate]: 'Certificado (ICP-Brasil)',
  [SignatureTypeCode.Electronic]: 'Eletrônica',
  [SignatureTypeCode.Handwritten]: 'Manuscrita',
};

const SIGNER_STATUS_LABEL: Record<number, string> = {
  [SignatureStatusCode.Pending]: 'Pendente',
  [SignatureStatusCode.Signed]: 'Assinado',
  [SignatureStatusCode.Rejected]: 'Recusado',
};

function requestStatusLabel(status: number): string {
  if (status === SignatureRequestStatusCode.Pending) {
    return 'Pendente';
  }
  if (status === SignatureRequestStatusCode.InProgress) {
    return 'Em andamento';
  }
  if (status === SignatureRequestStatusCode.Completed) {
    return 'Concluída';
  }
  if (status === SignatureRequestStatusCode.Cancelled) {
    return 'Cancelada';
  }
  return String(status);
}

function signatureRuleLabel(rule: number): string {
  if (rule === 0) {
    return 'Todas (All)';
  }
  if (rule === 1) {
    return 'Qualquer (Any)';
  }
  return String(rule);
}

function formatSignerSignedAt(signer: DocumentSignerDto): string {
  if (signer.status !== SignatureStatusCode.Signed) {
    return '—';
  }
  const formatted = formatDateTimeSafely(signer.signedAt);
  if (formatted) {
    return formatted;
  }
  return '—';
}

function isRequestEditable(sig: DocumentSignatureDto | null): boolean {
  if (!sig) {
    return false;
  }
  if (sig.status === SignatureRequestStatusCode.Completed) {
    return false;
  }
  if (sig.status === SignatureRequestStatusCode.Cancelled) {
    return false;
  }
  return true;
}

function normalizeDisplayIp(ipAddress: string): string {
  const trimmed = ipAddress.trim();
  const mappedPrefix = '::ffff:';
  if (trimmed.toLowerCase().startsWith(mappedPrefix)) {
    return trimmed.slice(mappedPrefix.length);
  }
  return trimmed;
}

export const DocumentSignersModal: React.FC<DocumentSignersModalProps> = ({
  isOpen,
  onClose,
  documentId,
  onChanged,
}) => {
  const { addToast } = useToast();
  const { copyText } = useCopyText();
  const [loading, setLoading] = useState(false);
  const [signature, setSignature] = useState<DocumentSignatureDto | null>(null);
  const [error, setError] = useState('');

  const [newName, setNewName] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [newSignatureType, setNewSignatureType] = useState<number>(SignatureTypeCode.Electronic);
  const [adding, setAdding] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [retryingPartialPdf, setRetryingPartialPdf] = useState(false);
  const [evidenceBySigner, setEvidenceBySigner] = useState<Record<string, SignatureEvidenceDto[]>>({});

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await documentSignatureService.getLatestByDocument(documentId);
      setSignature(data);
      if (!data) {
        setEvidenceBySigner({});
        setError('Não há solicitação de assinatura para este documento.');
      }
      else
      {
        const evidences = await documentSignatureService.getEvidence(data.id);
        const grouped: Record<string, SignatureEvidenceDto[]> = {};
        for (const item of evidences)
        {
          if (!grouped[item.documentSignerId])
          {
            grouped[item.documentSignerId] = [];
          }
          grouped[item.documentSignerId].push(item);
        }
        setEvidenceBySigner(grouped);
      }
    } catch (e) {
      setSignature(null);
      setEvidenceBySigner({});
      setError(e instanceof Error ? e.message : 'Não foi possível carregar a solicitação de assinatura.');
    } finally {
      setLoading(false);
    }
  }, [documentId]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    void load();
  }, [isOpen, load]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    setNewName('');
    setNewEmail('');
    setNewSignatureType(SignatureTypeCode.Electronic);
  }, [isOpen, documentId]);

  const handleRemove = async (signer: DocumentSignerDto) => {
    if (!signature) {
      return;
    }
    if (signer.status !== SignatureStatusCode.Pending) {
      addToast('Somente signatários pendentes podem ser removidos.', 'error');
      return;
    }
    try {
      await documentSignatureService.removeSigner(signature.id, signer.id);
      addToast('Signatário removido.', 'success');
      await load();
      await onChanged();
    } catch (e) {
      addToast(e instanceof Error ? e.message : 'Não foi possível remover o signatário.', 'error');
    }
  };

  const handleAdd = async () => {
    if (!signature) {
      return;
    }
    if (signature.signers.some(s => s.status === SignatureStatusCode.Signed)) {
      addToast('Não é possível incluir signatários após o início das assinaturas.', 'error');
      return;
    }
    const name = newName.trim();
    const email = newEmail.trim();
    if (!name) {
      addToast('Informe o nome do signatário.', 'error');
      return;
    }
    if (!email) {
      addToast('Informe o e-mail do signatário.', 'error');
      return;
    }

    setAdding(true);
    try {
      await documentSignatureService.addSigner(signature.id, {
        name,
        email,
        signatureType: newSignatureType,
      });
      addToast('Signatário adicionado.', 'success');
      setNewName('');
      setNewEmail('');
      await load();
      await onChanged();
    } catch (e) {
      addToast(e instanceof Error ? e.message : 'Não foi possível adicionar o signatário.', 'error');
    } finally {
      setAdding(false);
    }
  };

  const handleDownloadPartial = async () => {
    if (!signature) {
      return;
    }
    setDownloadingPdf(true);
    try {
      await documentSignatureService.downloadPartial(signature.id, 'documento-parcial-assinado.pdf');
      addToast('Prévia com assinaturas baixada com sucesso.', 'success');
    } catch (e) {
      addToast(e instanceof Error ? e.message : 'Não foi possível baixar a prévia parcial.', 'error');
    } finally {
      setDownloadingPdf(false);
    }
  };

  const handleDownloadFinal = async () => {
    if (!signature) {
      return;
    }
    setDownloadingPdf(true);
    try {
      await documentSignatureService.downloadSignedFinalPdf(signature.id, 'documento-assinado.pdf');
      addToast('Documento final baixado com sucesso.', 'success');
    } catch (e) {
      addToast(e instanceof Error ? e.message : 'Não foi possível baixar o documento final.', 'error');
    } finally {
      setDownloadingPdf(false);
    }
  };

  const handleRetryPartialPdf = async () => {
    if (!signature) {
      return;
    }

    setRetryingPartialPdf(true);
    try {
      const updated = await documentSignatureService.retryPartialPdf(signature.id);
      setSignature(updated);
      addToast('Geração da prévia com assinaturas reiniciada.', 'success');
      await onChanged();
    } catch (e) {
      addToast(e instanceof Error ? e.message : 'Não foi possível reiniciar a geração da prévia.', 'error');
    } finally {
      setRetryingPartialPdf(false);
    }
  };

  const editable = isRequestEditable(signature);
  const sortedSigners = [...(signature?.signers ?? [])].sort((a, b) => a.signatureOrder - b.signatureOrder);
  const hasAnySignedSigner = sortedSigners.some(s => s.status === SignatureStatusCode.Signed);
  const canAddSigners = editable && !hasAnySignedSigner;
  const signedCount = sortedSigners.filter(s => s.status === SignatureStatusCode.Signed).length;
  const totalSigners = sortedSigners.length;
  const showPartialDownload =
    signature?.status === SignatureRequestStatusCode.InProgress &&
    signature.partialSignedDocumentAvailable === true;
  const showPartialRetry =
    signature != null &&
    signature.partialSignedDocumentFailed === true &&
    signature.status !== SignatureRequestStatusCode.Cancelled &&
    signedCount > 0 &&
    signature.finalSignedDocumentAvailable !== true;
  const showFinalDownload =
    signature?.status === SignatureRequestStatusCode.Completed &&
    signature.finalSignedDocumentAvailable === true;
  const showProgressBadge =
    signature != null &&
    (signature.status === SignatureRequestStatusCode.InProgress ||
      signature.status === SignatureRequestStatusCode.Completed) &&
    totalSigners > 0;

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={loading || adding || downloadingPdf || retryingPartialPdf ? () => {} : onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-3xl transform overflow-hidden rounded-lg bg-white p-6 shadow-xl transition-all">
                <div className="flex items-start gap-3">
                  <UserGroupIcon className="h-8 w-8 shrink-0 text-indigo-600" aria-hidden />
                  <div>
                    <Dialog.Title className="text-lg font-semibold text-gray-900">Signatários</Dialog.Title>
                    <p className="mt-1 text-sm text-gray-600">
                      Gerencie os signatários da solicitação ativa. A API só permite alterações enquanto a solicitação
                      não estiver concluída ou cancelada; apenas signatários pendentes podem ser removidos. Não é
                      possível incluir novos signatários depois que pelo menos uma assinatura for registrada.
                    </p>
                  </div>
                </div>

                {loading ? (
                  <p className="mt-6 text-sm text-gray-600">Carregando…</p>
                ) : error ? (
                  <p className="mt-6 text-sm text-red-600" role="alert">
                    {error}
                  </p>
                ) : signature ? (
                  <div className="mt-6 space-y-6">
                    <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700">
                      <div className="flex flex-wrap items-center gap-2">
                        <span>
                          <span className="font-medium">Estado da solicitação:</span>{' '}
                          {requestStatusLabel(signature.status)}
                        </span>
                        <span className="text-gray-400">|</span>
                        <span>
                          <span className="font-medium">Regra:</span> {signatureRuleLabel(signature.signatureRule)}
                        </span>
                        <span className="text-gray-400">|</span>
                        <span>
                          <span className="font-medium">Mín. assinaturas:</span> {signature.minSignatures}
                        </span>
                        {showProgressBadge ? (
                          <span className="inline-flex items-center rounded-full border border-indigo-200 bg-indigo-50 px-2.5 py-0.5 text-xs font-medium text-indigo-700">
                            {signedCount} de {totalSigners} assinaturas coletadas
                          </span>
                        ) : null}
                      </div>
                      {showPartialDownload || showFinalDownload || showPartialRetry ? (
                        <div className="mt-3 flex flex-wrap gap-2">
                          {showPartialDownload ? (
                            <Button
                              type="button"
                              variant="secondary"
                              size="sm"
                              disabled={downloadingPdf || retryingPartialPdf}
                              onClick={() => void handleDownloadPartial()}
                            >
                              <ArrowDownTrayIcon className="h-5 w-5" aria-hidden />
                              {downloadingPdf
                                ? 'Baixando…'
                                : `Baixar prévia (${signedCount} de ${totalSigners} assinaturas)`}
                            </Button>
                          ) : null}
                          {showPartialRetry ? (
                            <Button
                              type="button"
                              variant="secondary"
                              size="sm"
                              disabled={downloadingPdf || retryingPartialPdf}
                              onClick={() => void handleRetryPartialPdf()}
                            >
                              {retryingPartialPdf ? 'Reiniciando…' : 'Tentar prévia novamente'}
                            </Button>
                          ) : null}
                          {showFinalDownload ? (
                            <Button
                              type="button"
                              variant="secondary"
                              size="sm"
                              disabled={downloadingPdf || retryingPartialPdf}
                              onClick={() => void handleDownloadFinal()}
                            >
                              <ArrowDownTrayIcon className="h-5 w-5" aria-hidden />
                              {downloadingPdf ? 'Baixando…' : 'Baixar documento final'}
                            </Button>
                          ) : null}
                        </div>
                      ) : null}
                      {showPartialRetry ? (
                        <p className="mt-2 text-xs text-amber-800">
                          A geração/atualização da prévia com assinaturas falhou. As assinaturas
                          coletadas permanecem válidas.
                        </p>
                      ) : null}
                    </div>

                    {signature.publicId ? (
                      <div className="rounded-md border border-indigo-100 bg-indigo-50/50 px-3 py-3 text-sm">
                        <p className="font-medium text-gray-900">ID público para validação</p>
                        <p className="mt-1 text-xs text-gray-600">
                          Use este identificador (ou o SHA-256 do PDF) no validador público.
                        </p>
                        <div className="mt-2 flex flex-wrap items-center gap-2">
                          <code className="break-all rounded bg-white px-2 py-1 font-mono text-xs text-gray-800 ring-1 ring-gray-200">
                            {signature.publicId}
                          </code>
                          <button
                            type="button"
                            onClick={() => void copyText(signature.publicId!)}
                            className="inline-flex items-center gap-1 rounded-md border border-gray-300 bg-white px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50"
                            title="Copiar ID público"
                          >
                            <ClipboardDocumentIcon className="h-4 w-4" aria-hidden />
                            Copiar ID
                          </button>
                          {signature.validationUrl ? (
                            <a
                              href={signature.validationUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs font-medium text-indigo-600 hover:text-indigo-800"
                            >
                              Abrir validador
                            </a>
                          ) : null}
                        </div>
                      </div>
                    ) : null}

                    <div className="overflow-x-auto rounded-md border border-gray-200">
                      <table className="min-w-full divide-y divide-gray-200 text-sm">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-3 py-2 text-left font-medium text-gray-700">Nome</th>
                            <th className="px-3 py-2 text-left font-medium text-gray-700">E-mail</th>
                            <th className="px-3 py-2 text-left font-medium text-gray-700">Tipo</th>
                            <th className="px-3 py-2 text-left font-medium text-gray-700">Estado</th>
                            <th className="px-3 py-2 text-left font-medium text-gray-700">Data da assinatura</th>
                            <th className="px-3 py-2 text-right font-medium text-gray-700">Ações</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 bg-white">
                          {sortedSigners.map(s => (
                            <tr key={s.id}>
                              <td className="px-3 py-2 text-gray-900">{s.name}</td>
                              <td className="px-3 py-2 text-gray-700">{s.email}</td>
                              <td className="px-3 py-2 text-gray-600">
                                {SIGNATURE_TYPE_LABEL[s.signatureType] ?? s.signatureType}
                              </td>
                              <td className="px-3 py-2 text-gray-600">
                                {SIGNER_STATUS_LABEL[s.status] ?? s.status}
                              </td>
                              <td className="px-3 py-2 whitespace-nowrap text-gray-600">
                                {formatSignerSignedAt(s)}
                              </td>
                              <td className="px-3 py-2 text-right">
                                {editable && s.status === SignatureStatusCode.Pending ? (
                                  <button
                                    type="button"
                                    onClick={() => void handleRemove(s)}
                                    className="text-sm font-medium text-red-600 hover:text-red-800"
                                  >
                                    Remover
                                  </button>
                                ) : (
                                  <span className="text-gray-400">—</span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {sortedSigners.some(s => s.status === SignatureStatusCode.Signed) ? (
                      <div className="rounded-md border border-gray-200 bg-white p-4">
                        <p className="text-sm font-medium text-gray-900">Evidências de assinatura</p>
                        <p className="mt-1 text-xs text-gray-500">
                          Dados técnicos do ato de assinar (data, IP do cliente e hash SHA-256 do documento no momento da
                          assinatura).
                        </p>
                        <ul className="mt-3 space-y-3">
                          {sortedSigners
                            .filter(s => s.status === SignatureStatusCode.Signed)
                            .map(s => {
                              const items = evidenceBySigner[s.id] ?? [];
                              return (
                                <li key={`evidence-${s.id}`} className="rounded border border-slate-100 bg-slate-50 px-3 py-2 text-sm">
                                  <p className="font-medium text-gray-800">{s.name}</p>
                                  {items.length === 0 ? (
                                    <p className="mt-1 text-xs text-gray-500">Nenhuma evidência técnica registrada.</p>
                                  ) : (
                                    <ul className="mt-2 space-y-3 text-xs text-gray-600">
                                      {items.map(ev => (
                                        <li key={ev.id} className="space-y-1.5">
                                          <p>
                                            <span className="font-medium text-gray-700">Data:</span>{' '}
                                            {formatDateTimeSafely(ev.timestamp) ?? ev.timestamp}
                                          </p>
                                          <p>
                                            <span className="font-medium text-gray-700">IP:</span>{' '}
                                            <span className="font-mono">{normalizeDisplayIp(ev.ipAddress)}</span>
                                          </p>
                                          <div className="flex flex-wrap items-start gap-2">
                                            <p className="min-w-0 flex-1 break-all">
                                              <span className="font-medium text-gray-700">Hash (SHA-256):</span>{' '}
                                              <span className="font-mono">{ev.documentHash}</span>
                                            </p>
                                            <button
                                              type="button"
                                              onClick={() => void copyText(ev.documentHash)}
                                              className="inline-flex shrink-0 items-center gap-1 rounded-md border border-gray-300 bg-white px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50"
                                              title="Copiar hash completo"
                                            >
                                              <ClipboardDocumentIcon className="h-4 w-4" aria-hidden />
                                              Copiar hash
                                            </button>
                                          </div>
                                        </li>
                                      ))}
                                    </ul>
                                  )}
                                </li>
                              );
                            })}
                        </ul>
                      </div>
                    ) : null}

                    {editable && hasAnySignedSigner ? (
                      <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                        Esta solicitação já teve assinatura registrada; não é possível incluir novos signatários.
                      </p>
                    ) : null}

                    {canAddSigners ? (
                      <div className="rounded-md border border-dashed border-indigo-200 bg-indigo-50/40 p-4">
                        <p className="text-sm font-medium text-gray-900">Adicionar signatário</p>
                        <div className="mt-3 grid gap-3 sm:grid-cols-2">
                          <div>
                            <label htmlFor="signer-name" className="block text-xs font-medium text-gray-700">
                              Nome
                            </label>
                            <input
                              id="signer-name"
                              type="text"
                              value={newName}
                              onChange={e => setNewName(e.target.value)}
                              disabled={adding}
                              className="mt-1 block w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 disabled:bg-gray-100"
                            />
                          </div>
                          <div>
                            <label htmlFor="signer-email" className="block text-xs font-medium text-gray-700">
                              E-mail
                            </label>
                            <input
                              id="signer-email"
                              type="email"
                              value={newEmail}
                              onChange={e => setNewEmail(e.target.value)}
                              disabled={adding}
                              className="mt-1 block w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 disabled:bg-gray-100"
                            />
                          </div>
                          <div className="sm:col-span-2">
                            <label htmlFor="signer-type" className="block text-xs font-medium text-gray-700">
                              Tipo de assinatura
                            </label>
                            <select
                              id="signer-type"
                              value={newSignatureType}
                              onChange={e => setNewSignatureType(Number(e.target.value))}
                              disabled={adding}
                              className="mt-1 block w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 disabled:bg-gray-100"
                            >
                              <option value={SignatureTypeCode.Electronic}>Eletrônica</option>
                              <option value={SignatureTypeCode.Certificate}>Certificado (ICP-Brasil)</option>
                              <option value={SignatureTypeCode.Handwritten}>Manuscrita</option>
                            </select>
                          </div>
                        </div>
                        <div className="mt-4 flex justify-end">
                          <button
                            type="button"
                            onClick={() => void handleAdd()}
                            disabled={adding}
                            className="inline-flex items-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:bg-indigo-400"
                          >
                            {adding ? 'Adicionando…' : 'Adicionar signatário'}
                          </button>
                        </div>
                      </div>
                    ) : null}
                  </div>
                ) : null}

                <div className="mt-6 flex justify-end gap-3 border-t border-gray-100 pt-4">
                  <button
                    type="button"
                    onClick={onClose}
                    disabled={loading || adding || downloadingPdf || retryingPartialPdf}
                    className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    Fechar
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};
